import { useMutation } from "@apollo/client";
import { IconNames } from "@blueprintjs/icons";
import { segmentTrackBusPageLoadOnlyBlocked } from "analytics/analyticTrackEvents";
import BackToProspectingNonIdealState from "components/common/BackToProspectingNonIdealState";
import LoadingFullScreen from "components/common/LoadingFullScreen";
import { PowerAmounts } from "components/detailPane/BusDetailPane/BusDetailPane";
import { useUserDataContext } from "contexts/UserDataContext/UserDataContext";
import {
    GetBusByNiraBusIdQuery,
    GetBusScopeViewsQuery,
    GetScopesForBusQuery,
    UnlockBusMutation,
    UnlockBusMutationVariables,
    useGetBusByNiraBusIdQuery,
    useGetScopesForBusQuery
} from "generated/graphql";
import { loader } from "graphql.macro";
import {
    assertMaybeToValue,
    getHasuraDataAndConvertToNiraType
} from "graphql/helpers/queryHelpers";
import { BUS_PAGE_FUEL_TYPE_PARAM } from "infrastructure/RouterApp";
import React, {
    createContext,
    PropsWithChildren,
    useContext,
    useEffect,
    useRef,
    useState
} from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { getDefaultBusFilter } from "types/busFilterTypes";
import { Bus, hasuraBusToNiraBus, NiraBusId } from "types/busType";
import {
    getComponentConfig,
    isPricedConstraintConfig,
    PricedConstraintConfig
} from "types/componentConfigPerRegion";
import {
    GeneratorType,
    GENERATOR_TYPES,
    SOLAR_TYPE
} from "types/generatorType";
import { Loading } from "types/loadingType";
import { RegionType } from "types/regionType";
import {
    convertHasuraScopesToNiraScopes,
    getScopeFromScopes,
    hasLocalStudyInProgress,
    isLocalStudiesEnabledForRegion,
    Scope,
    ScopeName
} from "types/scope";
import {
    convertToScopeViewType,
    getGenerationFuelTypesForRegion,
    isKnownScopeViewType,
    maybeGetConstraintStackScopeViewsFromRegionAndGenerator,
    ScopeViewType
} from "types/scopeViewType";
import {
    getLoadOnlyFlag,
    getLocalStudiesFlag
} from "types/teamFeatureFlagType";
import { isKnownVoltageType } from "types/voltageTypes";

const GET_BUS_SCOPE_VIEWS = loader("src/graphql/getBusScopeViews.graphql");
const UNLOCK_BUS = loader("src/graphql/unlockBus.graphql");

const DEFAULT_POWER_AMOUNTS: PowerAmounts = {
    energySize: 100,
    capacitySize: 100,
    chargingSize: 100
};

const STUDY_STATUS_POLL_INTERVAL_MS = 15_000;

type BusPageConfigurationStateType = {
    readonly niraBusId: NiraBusId;
    readonly bus: Bus;
    // Whether the user's team can kick off new studies (re-study) for this
    // bus's region. Viewing existing results is open to everyone.
    readonly canRestudy: boolean;
    readonly pricedConstraintConfig: PricedConstraintConfig;
    readonly selectedGeneratorType: GeneratorType;
    readonly setSelectedGeneratorType: (type: GeneratorType) => void;
    readonly allScopesForBus: ReadonlyArray<Scope>;
    readonly scopeToFuelTypesForBus: ReadonlyMap<
        ScopeName,
        ReadonlyArray<GeneratorType>
    >;
    readonly selectedScope: Scope | undefined;
    readonly setSelectedScope: (scope: ScopeName | undefined) => void;
    readonly powerAmounts: PowerAmounts;
    readonly setPowerAmounts: (powerAmounts: PowerAmounts) => void;
};

const BusPageConfigurationContext = createContext<
    BusPageConfigurationStateType | undefined
>(undefined);

const convertBusQueryResult = (data: GetBusByNiraBusIdQuery): Bus => {
    return hasuraBusToNiraBus(data.buses[0]);
};

const convertScopesQueryResult = (
    data: GetScopesForBusQuery
): ReadonlyArray<Scope> => {
    return convertHasuraScopesToNiraScopes(data.scopes);
};

const convertBusScopeViewsResult = (
    data: GetBusScopeViewsQuery
): ReadonlyMap<ScopeName, ReadonlyArray<ScopeViewType>> => {
    const map = new Map<ScopeName, ScopeViewType[]>();
    for (const row of data.buses_to_scopes) {
        if (!isKnownScopeViewType(row.scope_view)) {
            continue;
        }
        const existing = map.get(row.scope) ?? [];
        existing.push(convertToScopeViewType(row.scope_view));
        map.set(row.scope, existing);
    }
    return map;
};

// Known bug in ERCOT: in ERCOT, there are many scope views you need to support
// a given fuel type. However, buses_to_scopes is based on capacity costs, so it
// won't show all of them. This means we'll undershow the fuel types that work
// for a given scope.
const buildScopeToFuelTypes = (
    busScopeViews: ReadonlyMap<ScopeName, ReadonlyArray<ScopeViewType>>,
    region: RegionType
): ReadonlyMap<ScopeName, ReadonlyArray<GeneratorType>> => {
    const result = new Map<ScopeName, GeneratorType[]>();
    busScopeViews.forEach((scopeViews, scopeName) => {
        const supportedFuelTypes = GENERATOR_TYPES.filter((generatorType) => {
            const requiredScopeViews =
                maybeGetConstraintStackScopeViewsFromRegionAndGenerator(
                    region,
                    generatorType
                );
            if (requiredScopeViews === undefined) {
                return false;
            }
            return requiredScopeViews.every((required) =>
                scopeViews.includes(required)
            );
        });
        if (supportedFuelTypes.length > 0) {
            result.set(scopeName, supportedFuelTypes);
        }
    });
    return result;
};

export const getScopesForFuelType = (
    scopes: ReadonlyArray<Scope>,
    scopeToFuelTypes: ReadonlyMap<ScopeName, ReadonlyArray<GeneratorType>>,
    fuelType: GeneratorType
): ReadonlyArray<Scope> =>
    scopes.filter(
        (s) => scopeToFuelTypes.get(s.scope)?.includes(fuelType) ?? false
    );

type BusQueryState =
    | { readonly status: "loading" }
    | { readonly status: "error" }
    | { readonly status: "not_found" }
    | {
          readonly status: "found";
          readonly bus: Bus;
          readonly isLocked: boolean;
      };

const deriveBusState = (
    busLoading: boolean,
    busData: GetBusByNiraBusIdQuery | undefined,
    busError: unknown
): BusQueryState => {
    if (busLoading) {
        return { status: "loading" };
    }
    if (busError || !busData) {
        return { status: "error" };
    }
    if (busData.buses.length === 0) {
        return { status: "not_found" };
    }
    if (!isKnownVoltageType(assertMaybeToValue(busData.buses[0].voltage))) {
        console.warn(
            `Bus ${busData.buses[0].nira_bus_id} has an unsupported voltage: ${busData.buses[0].voltage}`
        );
        return { status: "error" };
    }
    return {
        status: "found",
        bus: convertBusQueryResult(busData),
        isLocked: busData.buses[0].unlocked.length === 0
    };
};

// Poll so study results appear without a reload. Gated on actually having an
// in-progress study.
const useScopesForBus = (
    niraBusId: NiraBusId
): Loading<ReadonlyArray<Scope>> => {
    // By default, loading only changes on the first fetch and not on subsequent polls,
    // so the page won't flicker
    const { data, loading, startPolling, stopPolling } =
        useGetScopesForBusQuery({ variables: { niraBusId } });

    const scopes: Loading<ReadonlyArray<Scope>> =
        loading || data === undefined
            ? "loading"
            : convertScopesQueryResult(data);

    const hasStudyInProgress =
        scopes !== "loading" && hasLocalStudyInProgress(scopes);

    useEffect(() => {
        if (!hasStudyInProgress) {
            return undefined;
        }
        startPolling(STUDY_STATUS_POLL_INTERVAL_MS);
        return stopPolling;
    }, [startPolling, stopPolling, hasStudyInProgress]);

    return scopes;
};

export const BusPageConfigurationProvider: React.FC<PropsWithChildren> = ({
    children
}) => {
    const { niraBusId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const fuelTypeFromUrl = searchParams.get(BUS_PAGE_FUEL_TYPE_PARAM);
    const setSelectedGeneratorType = (type: GeneratorType) => {
        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.set(BUS_PAGE_FUEL_TYPE_PARAM, type);
        setSearchParams(nextSearchParams, { replace: true });
    };
    const [selectedScopeName, setSelectedScopeName] = useState<
        ScopeName | undefined
    >(undefined);
    const [powerAmounts, setPowerAmounts] = useState<PowerAmounts>(
        DEFAULT_POWER_AMOUNTS
    );
    // Tracks whether the one-shot auto-unlock (below) has finished. Buses are
    // gated behind an `unlocked_buses` row in Hasura; the map creates that row
    // on select, but deep-linking straight to the bus page never does. For
    // unlimited subscriptions we create it here so the study results and
    // Related Queue Projects panel load instead of coming back empty.
    const [autoUnlockDone, setAutoUnlockDone] = useState(false);

    if (!niraBusId) {
        throw new Error(
            "BusPageConfigurationProvider requires a niraBusId param"
        );
    }

    const user = useUserDataContext();
    const {
        data: busData,
        loading: busLoading,
        error: busError,
        refetch: refetchBus
    } = useGetBusByNiraBusIdQuery({
        variables: { niraBusId }
    });

    const scopes = useScopesForBus(niraBusId);

    const busScopeViews: Loading<
        ReadonlyMap<ScopeName, ReadonlyArray<ScopeViewType>>
    > = getHasuraDataAndConvertToNiraType(
        GET_BUS_SCOPE_VIEWS,
        convertBusScopeViewsResult,
        { niraBusId }
    );

    const busState = deriveBusState(busLoading, busData, busError);

    const busRegion =
        busState.status === "found" ? busState.bus.region : undefined;
    // Viewing the bus page is open to everyone in supported regions.
    // Re-studying is separately gated by the local_studies flag — see
    // canRestudy.
    const isBusPageAvailableForRegion =
        busRegion !== undefined && isLocalStudiesEnabledForRegion(busRegion);
    // The bus page only studies generation fuel types, so a load-only team
    // (load_only:<REGION>) has nothing to see here. Checked before the
    // auto-unlock so we never spend an unlock row on a page they can't use.
    const isLoadOnlyForRegion =
        busRegion !== undefined &&
        user.teamFeatureFlags.includes(getLoadOnlyFlag(busRegion));
    const isBlockedForLoadOnly =
        busState.status === "found" &&
        isBusPageAvailableForRegion &&
        isLoadOnlyForRegion;
    useEffect(() => {
        if (isBlockedForLoadOnly && busRegion !== undefined) {
            segmentTrackBusPageLoadOnlyBlocked(niraBusId, busRegion);
        }
    }, [isBlockedForLoadOnly, niraBusId, busRegion]);
    const regionSubscription =
        busRegion !== undefined
            ? user.subscriptions.find((s) => s.region === busRegion)
            : undefined;
    const isUnlimited = regionSubscription?.unlimited ?? false;
    // An unlimited-subscription user landed (e.g. via a shared link) on a bus
    // their team never unlocked. Hasura requires an `unlocked_buses` row before
    // it will return constraint/queue data, so without an explicit unlock the
    // whole results area (and the DFAX box) silently comes back empty. Unlock
    // is idempotent and unlimited subs unlock for free, so we auto-create the
    // row here (the effect below) before the lock check. If it fails, the lock
    // check still catches it and shows the "Bus not unlocked" page.
    const needsAutoUnlock =
        busState.status === "found" &&
        isBusPageAvailableForRegion &&
        !isLoadOnlyForRegion &&
        isUnlimited &&
        busState.isLocked;

    const [unlockBus] = useMutation<
        UnlockBusMutation,
        UnlockBusMutationVariables
    >(UNLOCK_BUS);
    const autoUnlockAttempted = useRef(false);
    useEffect(() => {
        if (!needsAutoUnlock || autoUnlockAttempted.current) {
            return;
        }
        autoUnlockAttempted.current = true;
        // Unlock, then refetch the bus so `isLocked` reflects the new
        // `unlocked_buses` row before we open the gate. On success the lock
        // check below passes; if the unlock (or refetch) fails, the bus stays
        // locked and the user gets the clear "Bus not unlocked" page instead
        // of a silently-empty results area. Either way we set the flag so the
        // loading gate never hangs.
        unlockBus({ variables: { niraBusId } })
            .then(() => refetchBus())
            .catch(() => undefined)
            .finally(() => setAutoUnlockDone(true));
    }, [needsAutoUnlock, niraBusId, unlockBus, refetchBus]);

    if (busState.status === "error") {
        return (
            <BackToProspectingNonIdealState
                icon={IconNames.ERROR}
                title="Something went wrong"
                description="An error occurred while loading bus data. Please try again later."
            />
        );
    }

    if (busState.status === "not_found") {
        return (
            <BackToProspectingNonIdealState
                icon={IconNames.ERROR}
                title="Bus not found"
                description="The requested bus does not exist or you do not have access to view it."
            />
        );
    }

    if (busState.status === "found") {
        if (!isBusPageAvailableForRegion) {
            return (
                <BackToProspectingNonIdealState
                    icon={IconNames.LOCK}
                    title="Access not available"
                    description="The bus page is not available in this region yet."
                />
            );
        }

        if (isLoadOnlyForRegion) {
            return (
                <BackToProspectingNonIdealState
                    icon={IconNames.LOCK}
                    title="Not available for load"
                    description="The bus page studies generation projects and is not available on a load-only subscription. Contact Nira to add generation to your subscription."
                />
            );
        }

        // Unlimited sub on a not-yet-unlocked bus: hold on the loading screen
        // while the one-shot auto-unlock (fired in the effect above) lands the
        // `unlocked_buses` row and the bus refetches. This runs BEFORE the lock
        // check so a successful unlock clears `isLocked` first.
        if (needsAutoUnlock && !autoUnlockDone) {
            return <LoadingFullScreen />;
        }

        // Applies to everyone, including unlimited subs: if the bus is still
        // locked here (auto-unlock didn't run, or it failed), show the lock
        // page rather than a silently-empty results area.
        if (busState.isLocked) {
            return (
                <BackToProspectingNonIdealState
                    icon={IconNames.LOCK}
                    title="Bus not unlocked"
                    description="This bus has not been unlocked for your team. Unlock the bus from the Prospecting map to view details and create local studies."
                />
            );
        }
    }

    if (
        busState.status === "loading" ||
        scopes === "loading" ||
        busScopeViews === "loading"
    ) {
        return <LoadingFullScreen />;
    }

    const { bus } = busState;

    const selectedGeneratorType =
        getGenerationFuelTypesForRegion(bus.region).find(
            (type) => type === fuelTypeFromUrl
        ) ?? SOLAR_TYPE;

    const componentConfig = getComponentConfig(
        bus.region,
        selectedGeneratorType
    );
    if (!isPricedConstraintConfig(componentConfig)) {
        throw new Error("Bus page does not support curtailment regions");
    }

    const scopeToFuelTypesForBus = buildScopeToFuelTypes(
        busScopeViews,
        bus.region
    );

    // Resolve the selected scope: explicit user selection first, falling
    // back to the region's canonical scope — the same default the
    // prospecting map uses — so the page always lands with results instead
    // of an empty pick-a-scope state. When the canonical scope has no data
    // for the selected fuel type at this bus, getDefaultBusFilter falls back
    // to the first scope that does. With no scope at this bus for the
    // selected fuel type, no scope is selected, matching the picker's "No
    // scopes available" state. SOLAR is used for the lookup because it
    // is defined for every region (the canonical scope does not vary by
    // generator type in bus-page regions).
    const scopesForSelectedFuelType = getScopesForFuelType(
        scopes,
        scopeToFuelTypesForBus,
        selectedGeneratorType
    );
    const defaultScopeName =
        scopesForSelectedFuelType.length > 0
            ? getDefaultBusFilter(
                  bus.region,
                  SOLAR_TYPE,
                  scopesForSelectedFuelType
              ).scope
            : undefined;
    const selectedScope =
        (selectedScopeName !== undefined
            ? getScopeFromScopes(scopes, selectedScopeName)
            : undefined) ??
        (defaultScopeName === undefined
            ? undefined
            : getScopeFromScopes(scopes, defaultScopeName));

    const canRestudy = user.teamFeatureFlags.includes(
        getLocalStudiesFlag(bus.region)
    );

    const contextValue: BusPageConfigurationStateType = {
        niraBusId,
        bus,
        canRestudy,
        pricedConstraintConfig: componentConfig,
        selectedGeneratorType,
        setSelectedGeneratorType,
        allScopesForBus: scopes,
        scopeToFuelTypesForBus,
        selectedScope,
        setSelectedScope: setSelectedScopeName,
        powerAmounts,
        setPowerAmounts
    };

    return (
        <BusPageConfigurationContext.Provider value={contextValue}>
            {children}
        </BusPageConfigurationContext.Provider>
    );
};

export const useBusPageConfiguration = (): BusPageConfigurationStateType => {
    const context = useContext(BusPageConfigurationContext);
    if (context === undefined) {
        throw new Error(
            "useBusPageConfiguration must be used within a BusPageConfigurationProvider"
        );
    }
    return context;
};
